/**
 * SuperSettings — `/super/settings` — platform-wide config:
 *   • Razorpay keys (placeholders for now; mock mode toggle)
 *   • Indian GST seller info (legal name, GSTIN, PAN, address, state code)
 *   • Invoice prefix and the next sequence
 *   • Default SMS / WhatsApp per-message rate
 */
import { useEffect, useState } from "react";
import { Save, Loader2, KeyRound, Building2, FileText, Bike, RefreshCw, Cloud, CheckCircle2, AlertCircle, Upload as UploadIcon, Globe } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Field } from "../admin/AdminRestaurants";

export default function SuperSettings() {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    const [bizilgo, setBizilgo] = useState(null);
    const [bizilgoBusy, setBizilgoBusy] = useState(false);
    const [storage, setStorage] = useState(null);
    const [testFile, setTestFile] = useState(null);
    const [testBusy, setTestBusy] = useState(false);

    const refreshStorage = () => {
        api.get("/super/settings/storage-health").then(({ data }) => setStorage(data)).catch(() => {});
    };

    useEffect(() => {
        api.get("/super/settings").then(({ data }) => setS(data));
        api.get("/super/bizilgo/status").then(({ data }) => setBizilgo(data)).catch(() => {});
        refreshStorage();
    }, []);

    const registerWebhook = async () => {
        setBizilgoBusy(true);
        try {
            // Backend can't read REACT_APP_BACKEND_URL (that's a frontend-only
            // env var). We send the callback URL explicitly so the no-body
            // auto-derive failure path isn't hit.
            const callbackUrl = `${window.location.origin}/api/webhook/bizilgo`;
            const { data } = await api.post("/super/bizilgo/register-webhook", { callback_url: callbackUrl });
            if (data.success) {
                toast.success(`Webhook registered: ${data.callback_url}`);
            } else {
                toast.error(`Could not reach BizilGO: ${data.response?.error || data.response?.detail || "unknown"}`);
            }
            // Refresh status panel.
            api.get("/super/bizilgo/status").then(({ data }) => setBizilgo(data));
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Failed to register webhook");
        } finally { setBizilgoBusy(false); }
    };

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.patch("/super/settings", s);
            setS(data);
            toast.success("Saved");
            refreshStorage();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const tryUpload = async () => {
        if (!testFile) return toast.error("Pick an image first");
        setTestBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", testFile);
            const { data } = await api.post("/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Uploaded! ${data.url}`);
            window.open(data.url, "_blank");
        } catch (e) {
            // Pull the most actionable string out of the error response.
            // Prod 502s from Cloudflare may strip our `detail` — fall back
            // to the raw HTTP status / message so the user has a clue.
            const detail = e?.response?.data?.detail
                || e?.response?.data?.message
                || (e?.response?.status ? `HTTP ${e.response.status}` : null)
                || e?.message
                || "Upload failed";
            // Toasts truncate — keep first 240 chars but mirror full detail in console.
            console.error("upload failed:", e?.response?.data || e);
            toast.error(String(detail).slice(0, 240), { duration: 8000 });
        } finally { setTestBusy(false); }
    };

    if (!s) return <p className="text-muted-foreground">Loading…</p>;
    const upd = (k, v) => setS({ ...s, [k]: v });
    const updAddr = (k, v) => setS({ ...s, seller_address: { ...(s.seller_address || {}), [k]: v } });

    return (
        <div data-testid="super-settings" className="max-w-3xl">
            <h1 className="text-2xl font-bold">Platform Settings</h1>
            <p className="text-sm text-muted-foreground mt-1">Razorpay keys · GST seller details · Invoicing config</p>

            <Section icon={KeyRound} title="Razorpay (platform-side)" subtitle="Used to receive subscription payments from tenants. Keep mock ON until your live keys are configured.">
                <div className="grid grid-cols-2 gap-3">
                    <Field full label="Razorpay Key ID"><input data-testid="rzp-key-id" className="inp font-mono" value={s.razorpay_key_id || ""} onChange={(e) => upd("razorpay_key_id", e.target.value)} placeholder="rzp_live_xxx" /></Field>
                    <Field full label="Razorpay Secret"><input data-testid="rzp-secret" type="password" className="inp font-mono" value={s.razorpay_key_secret || ""} onChange={(e) => upd("razorpay_key_secret", e.target.value)} placeholder="••••••••••" /></Field>
                </div>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer mt-3">
                    <input data-testid="rzp-mock" type="checkbox" className="accent-primary w-4 h-4" checked={!!s.razorpay_mock} onChange={(e) => upd("razorpay_mock", e.target.checked)} />
                    <span className="font-semibold">Mock mode</span>
                    <span className="text-xs text-muted-foreground">(top-ups credit instantly; no live charge)</span>
                </label>
            </Section>

            <Section icon={Cloud} title="Image Storage (AWS S3)" subtitle="Used by /api/uploads → product images, banners, branding logos. Falls back to env vars when blank.">
                <div className="grid grid-cols-2 gap-3">
                    <Field full label="AWS Access Key ID">
                        <input data-testid="aws-key" className="inp font-mono" value={s.aws_access_key_id || ""}
                               onChange={(e) => upd("aws_access_key_id", e.target.value)} placeholder="AKIA…" />
                    </Field>
                    <Field full label="AWS Secret Access Key">
                        <input data-testid="aws-secret" type="password" className="inp font-mono" value={s.aws_secret_access_key || ""}
                               onChange={(e) => upd("aws_secret_access_key", e.target.value)} placeholder="••••••••••" />
                    </Field>
                    <Field label="Region">
                        <input data-testid="aws-region" className="inp font-mono" value={s.aws_region || ""}
                               onChange={(e) => upd("aws_region", e.target.value)} placeholder="ap-south-1" />
                    </Field>
                    <Field label="Bucket name">
                        <input data-testid="aws-bucket" className="inp font-mono" value={s.aws_bucket_name || ""}
                               onChange={(e) => upd("aws_bucket_name", e.target.value)} placeholder="bizil-media-prod" />
                    </Field>
                    <Field full label="Public CDN host (optional)">
                        <input data-testid="aws-cdn" className="inp font-mono" value={s.aws_public_media_host || ""}
                               onChange={(e) => upd("aws_public_media_host", e.target.value)} placeholder="media.bizil.app — fronts the bucket via CloudFront/CDN" />
                    </Field>
                </div>

                <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Diagnostics</p>
                        <button data-testid="aws-refresh" onClick={refreshStorage}
                                className="text-[11px] font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                            <RefreshCw size={11} /> Refresh
                        </button>
                    </div>
                    {storage ? (
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <StatusRow label="Upload pipeline" value={storage.ready ? "Ready" : "Not configured"} ok={storage.ready} testid="aws-status-ready" />
                            <StatusRow label="Region" value={storage.region || "—"} ok={!!storage.region} testid="aws-status-region" />
                            <StatusRow label="Bucket" value={storage.bucket || "—"} ok={!!storage.bucket} testid="aws-status-bucket" />
                            <StatusRow label="CDN host" value={storage.public_host || "(direct s3 url)"} ok={!!storage.public_host} testid="aws-status-cdn" />
                            <StatusRow label="Access key" value={storage.access_key_preview || "—"} ok={!!storage.access_key_preview} testid="aws-status-key" />
                            <StatusRow label="Source" value={Object.values(storage.sources || {}).every(v => v === "mongo") ? "Mongo (saved here)" : "Mongo + env mix"} ok testid="aws-status-source" />
                        </div>
                    ) : <p className="text-xs text-muted-foreground">Loading…</p>}

                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <input data-testid="aws-test-file" type="file" accept="image/*"
                               onChange={(e) => setTestFile(e.target.files?.[0] || null)}
                               className="text-xs" />
                        <button data-testid="aws-test-upload" onClick={tryUpload} disabled={testBusy || !testFile || !storage?.ready}
                                className="bg-foreground text-background rounded-lg px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-40">
                            {testBusy ? <Loader2 size={12} className="animate-spin" /> : <UploadIcon size={12} />}
                            Test upload
                        </button>
                        <span className="text-[11px] text-muted-foreground">
                            Picks a file → uploads via `POST /api/uploads` → opens the public URL.
                        </span>
                    </div>
                </div>
            </Section>

            <Section icon={Building2} title="GST seller details" subtitle="Stamped onto every invoice PDF.">
                <div className="grid grid-cols-2 gap-3">
                    <Field full label="Legal name"><input className="inp" value={s.seller_legal_name || ""} onChange={(e) => upd("seller_legal_name", e.target.value)} /></Field>
                    <Field label="GSTIN"><input data-testid="seller-gstin" className="inp font-mono uppercase" value={s.seller_gstin || ""} onChange={(e) => upd("seller_gstin", e.target.value.toUpperCase())} placeholder="36AABCB1234M1ZX" /></Field>
                    <Field label="PAN"><input className="inp font-mono uppercase" value={s.seller_pan || ""} onChange={(e) => upd("seller_pan", e.target.value.toUpperCase())} placeholder="AABCB1234M" /></Field>
                    <Field label="State code"><input className="inp" value={s.seller_state_code || ""} onChange={(e) => upd("seller_state_code", e.target.value)} placeholder="36 = Telangana" /></Field>
                    <Field label="Email"><input className="inp" value={s.seller_email || ""} onChange={(e) => upd("seller_email", e.target.value)} /></Field>
                    <Field label="Phone"><input className="inp" value={s.seller_phone || ""} onChange={(e) => upd("seller_phone", e.target.value)} /></Field>
                    <Field full label="Address line 1"><input className="inp" value={s.seller_address?.line1 || ""} onChange={(e) => updAddr("line1", e.target.value)} /></Field>
                    <Field full label="Address line 2"><input className="inp" value={s.seller_address?.line2 || ""} onChange={(e) => updAddr("line2", e.target.value)} /></Field>
                    <Field label="City"><input className="inp" value={s.seller_address?.city || ""} onChange={(e) => updAddr("city", e.target.value)} /></Field>
                    <Field label="State"><input className="inp" value={s.seller_address?.state || ""} onChange={(e) => updAddr("state", e.target.value)} /></Field>
                    <Field label="Pincode"><input className="inp" value={s.seller_address?.pincode || ""} onChange={(e) => updAddr("pincode", e.target.value)} /></Field>
                    <Field label="Country"><input className="inp" value={s.seller_address?.country || ""} onChange={(e) => updAddr("country", e.target.value)} /></Field>
                </div>
            </Section>

            <Section icon={FileText} title="Invoicing & messaging rates" subtitle="Defaults applied to new plans / fallback when a plan doesn't override.">
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Invoice prefix"><input className="inp" value={s.invoice_prefix || "INV"} onChange={(e) => upd("invoice_prefix", e.target.value)} /></Field>
                    <Field label="Next invoice #"><input type="number" className="inp" value={s.next_invoice_seq || 1} onChange={(e) => upd("next_invoice_seq", Number(e.target.value))} /></Field>
                    <div />
                    <Field label="Default SMS rate ₹"><input type="number" step="0.01" className="inp" value={s.sms_rate_default ?? 0.15} onChange={(e) => upd("sms_rate_default", Number(e.target.value))} /></Field>
                    <Field label="Default WA rate ₹"><input type="number" step="0.01" className="inp" value={s.whatsapp_rate_default ?? 0.40} onChange={(e) => upd("whatsapp_rate_default", Number(e.target.value))} /></Field>
                </div>
            </Section>

            <Section icon={Globe} title="DNS · GoDaddy auto-link" subtitle="Push CNAME records to your GoDaddy account directly from the tenant Branding panel. Without these, super admins must add records manually on the registrar.">
                <GoDaddyPanel />
            </Section>

            <Section icon={Bike} title="BizilGO Rider Integration" subtitle="External rider pool at bizilgo.com. Tenants on BIZILGO or BOTH mode auto-dispatch orders here on READY.">
                {bizilgo ? (
                    <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Base URL</div>
                                <div className="font-mono text-xs mt-1 break-all" data-testid="bizilgo-base-url">{bizilgo.base_url}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Webhook secret</div>
                                <div className="font-mono text-xs mt-1">{bizilgo.webhook_token_set ? "•••••••• (configured)" : <span className="text-amber-600">not configured</span>}</div>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Enabled tenants ({bizilgo.enabled_tenants?.length || 0})</div>
                            {bizilgo.enabled_tenants?.length ? (
                                <ul className="text-xs space-y-1">
                                    {bizilgo.enabled_tenants.map((t) => (
                                        <li key={t.id} className="flex items-center gap-2">
                                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" />
                                            <span className="font-semibold">{t.name}</span>
                                            <span className="text-muted-foreground">/{t.slug}</span>
                                            <span className="text-[10px] font-bold uppercase bg-violet-100 text-violet-700 rounded-full px-2 py-0.5">{t.rider_mode}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <p className="text-xs text-muted-foreground">No tenants on BizilGO mode yet — toggle one on the Tenants page.</p>}
                        </div>
                        <button
                            data-testid="bizilgo-register-webhook"
                            onClick={registerWebhook}
                            disabled={bizilgoBusy}
                            className="bg-violet-600 text-white rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {bizilgoBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                            Register webhook with BizilGO
                        </button>
                        <p className="text-[11px] text-muted-foreground">
                            This tells BizilGO where to POST status updates. Re-run if the public URL or webhook secret changes.
                        </p>
                    </div>
                ) : <p className="text-xs text-muted-foreground">Loading…</p>}
            </Section>

            <button data-testid="save-settings" onClick={save} disabled={busy} className="mt-6 bg-primary text-primary-foreground rounded-xl px-6 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-50">{busy && <Loader2 size={16} className="animate-spin" />}<Save size={16} /> Save settings</button>

            <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.6rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
        </div>
    );
}

function GoDaddyPanel() {
    const [cfg, setCfg] = useState(null);
    const [key, setKey] = useState("");
    const [secret, setSecret] = useState("");
    const [target, setTarget] = useState("");
    const [busy, setBusy] = useState(false);
    const [domains, setDomains] = useState(null);
    const [domainsBusy, setDomainsBusy] = useState(false);
    const platformHost = typeof window !== "undefined" ? window.location.host : "food.localappstore.in";

    useEffect(() => {
        api.get("/super/settings/platform").then(({ data }) => {
            setCfg(data);
            setTarget(data?.default_cname_target || "");
        }).catch(() => {});
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            const body = { default_cname_target: target || null };
            if (key) body.godaddy_api_key = key;
            if (secret) body.godaddy_api_secret = secret;
            const { data } = await api.patch("/super/settings/platform", body);
            setCfg(data);
            setKey(""); setSecret("");
            toast.success("GoDaddy credentials saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save");
        } finally { setBusy(false); }
    };

    const loadDomains = async () => {
        setDomainsBusy(true);
        try {
            const { data } = await api.get("/super/godaddy/domains");
            setDomains(data);
            toast.success(`Loaded ${data.length} domain(s) from GoDaddy`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load domains");
            setDomains([]);
        } finally { setDomainsBusy(false); }
    };

    if (!cfg) return <p className="text-xs text-muted-foreground">Loading…</p>;
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <Field full label="GoDaddy API key">
                    <input
                        data-testid="godaddy-key"
                        className="inp font-mono"
                        placeholder={cfg.godaddy_api_key_preview || "(not configured)"}
                        value={key}
                        onChange={(e) => setKey(e.target.value)}
                    />
                </Field>
                <Field full label="GoDaddy API secret">
                    <input
                        data-testid="godaddy-secret"
                        type="password"
                        className="inp font-mono"
                        placeholder={cfg.godaddy_api_secret_preview || "(not configured)"}
                        value={secret}
                        onChange={(e) => setSecret(e.target.value)}
                    />
                </Field>
                <Field full label={`Default CNAME target (leave blank to use current host: ${platformHost})`}>
                    <input
                        data-testid="godaddy-target"
                        className="inp font-mono"
                        placeholder={platformHost}
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                        ⚠️ This must be the actual host serving your app (currently <code className="font-bold">{platformHost}</code>),
                        not a bare apex like <code>localappstore.in</code>. Tenant CNAMEs point HERE
                        so customers land on your live app. Leave blank to auto-use the current host.
                    </p>
                </Field>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <button
                    data-testid="godaddy-save"
                    onClick={save}
                    disabled={busy}
                    className="bg-foreground text-background rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50"
                >
                    {busy && <Loader2 size={14} className="animate-spin" />}
                    <Save size={14} /> Save GoDaddy creds
                </button>
                <button
                    data-testid="godaddy-load-domains"
                    onClick={loadDomains}
                    disabled={domainsBusy || !cfg.godaddy_configured}
                    className="bg-emerald-600 text-white rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-40"
                    title={cfg.godaddy_configured ? "Fetch domain list" : "Save credentials first"}
                >
                    {domainsBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    List my GoDaddy domains
                </button>
                {cfg.godaddy_configured ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
                        <CheckCircle2 size={12} /> Credentials saved
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-semibold">
                        <AlertCircle size={12} /> Not configured
                    </span>
                )}
            </div>
            {domains && (
                <div className="bg-background rounded-xl border border-border p-3" data-testid="godaddy-domains">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                        Domains on this GoDaddy account ({domains.length})
                    </p>
                    {domains.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No active domains found. Buy or transfer one to GoDaddy, then refresh.</p>
                    ) : (
                        <ul className="text-xs grid grid-cols-2 gap-1.5">
                            {domains.map((d) => (
                                <li key={d.domain} className="font-mono">
                                    <span className="font-semibold">{d.domain}</span>
                                    <span className="text-muted-foreground ml-2">expires {String(d.expires || "").slice(0, 10)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            <p className="text-[11px] text-muted-foreground">
                Once saved, the green <b>Auto-link with GoDaddy</b> button on each tenant&apos;s
                Branding panel will push the CNAME record automatically &mdash; no manual DNS
                edits required.
            </p>
        </div>
    );
}

function Section({ icon: Icon, title, subtitle, children }) {
    return (
        <section className="mt-8 bg-card border border-border rounded-2xl p-5">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon size={18} /></div>
                <div>
                    <h2 className="font-bold text-lg">{title}</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
                </div>
            </div>
            {children}
        </section>
    );
}


function StatusRow({ label, value, ok, testid }) {
    return (
        <div data-testid={testid} className={`bg-background rounded-lg border border-border px-2.5 py-1.5 flex items-center gap-2`}>
            {ok ? <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                : <AlertCircle size={13} className="text-amber-500 shrink-0" />}
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</span>
            <span className="font-semibold truncate flex-1 text-right">{value}</span>
        </div>
    );
}
